export type VoucherTemplateElementType =
    'TEXT' | 'AMOUNT' | 'CODE' | 'SENDER' | 'RECEIVER';

export type VoucherTemplateLanguage = 'DE' | 'EN' | 'IT' | 'FR';

/**
 * iText alignment constants:
 * 0 = LEFT, 1 = CENTER, 2 = RIGHT
 */
export type VoucherTemplateAlignment = 0 | 1 | 2;

/**
 * Internal frontend representation of a template element.
 * Used throughout the editor UI.
 */
export type VoucherTemplateElement = {
    elementType: VoucherTemplateElementType;
    x: number;
    y: number;
    w: number;
    h: number;
    rotation: number;
    font: string;
    fontSize: number;
    bold: boolean;
    italic: boolean;
    colorR: number;
    colorG: number;
    colorB: number;
    alignment: VoucherTemplateAlignment;
    outlineWidth?: number;
    outlineColorR?: number;
    outlineColorG?: number;
    outlineColorB?: number;
};

/**
 * Backend API representation of a template element.
 * Color is nested as { r, g, b } instead of flat colorR/colorG/colorB.
 */
export type BackendTemplateElementDTO = {
    x: number;
    y: number;
    w: number;
    h: number;
    rotation: number;
    font: string;
    fontSize: number;
    bold: boolean;
    italic: boolean;
    color: { r: number; g: number; b: number };
    alignment: VoucherTemplateAlignment;
    outlineWidth?: number;
    outlineColor?: { r: number; g: number; b: number } | null;
};

/**
 * Convert backend element DTO to internal frontend format.
 */
export const fromBackendElement = (
    elementType: VoucherTemplateElementType,
    dto: BackendTemplateElementDTO
): VoucherTemplateElement => ({
    elementType,
    x: dto.x,
    y: dto.y,
    w: dto.w,
    h: dto.h,
    rotation: dto.rotation,
    font: dto.font,
    fontSize: dto.fontSize,
    bold: dto.bold,
    italic: dto.italic,
    colorR: dto.color.r,
    colorG: dto.color.g,
    colorB: dto.color.b,
    alignment: dto.alignment,
    outlineWidth: dto.outlineWidth,
    outlineColorR: dto.outlineColor?.r,
    outlineColorG: dto.outlineColor?.g,
    outlineColorB: dto.outlineColor?.b,
});

/**
 * Convert internal frontend element to backend format.
 */
export const toBackendElement = (
    el: VoucherTemplateElement
): BackendTemplateElementDTO => ({
    x: el.x,
    y: el.y,
    w: el.w,
    h: el.h,
    rotation: el.rotation,
    font: el.font,
    fontSize: el.fontSize,
    bold: el.bold,
    italic: el.italic,
    color: { r: el.colorR, g: el.colorG, b: el.colorB },
    alignment: el.alignment,
    outlineWidth: el.outlineWidth,
    outlineColor:
        el.outlineWidth && el.outlineWidth > 0
            ? {
                  r: el.outlineColorR ?? 0,
                  g: el.outlineColorG ?? 0,
                  b: el.outlineColorB ?? 0,
              }
            : null,
});

export type VoucherTemplateAdminDTO = {
    id: string;
    liftOperatorId: string | null;
    file: string | null;
    language: VoucherTemplateLanguage;
    name: string;
    defaultText: string;
    source: string;
    active: boolean;
};

export type VoucherTemplateAdminDetailDTO = VoucherTemplateAdminDTO & {
    elements: Record<string, BackendTemplateElementDTO>;
};

export type CreateVoucherTemplateCommand = {
    liftOperatorId?: string | null;
    language: VoucherTemplateLanguage;
    name: string;
    defaultText: string;
    elements: Record<VoucherTemplateElementType, BackendTemplateElementDTO>;
};

export type UpdateVoucherTemplateCommand = {
    name: string;
    defaultText: string;
    language: VoucherTemplateLanguage;
    elements: Record<VoucherTemplateElementType, BackendTemplateElementDTO>;
};

export type SetActiveCommand = {
    active: boolean;
};

export type VoucherFontDTO = {
    id: string;
    liftOperatorId: string | null;
    name: string;
    fileName: string;
    fileExtension: string;
};

export const ELEMENT_TYPES: VoucherTemplateElementType[] = [
    'TEXT',
    'AMOUNT',
    'CODE',
    'SENDER',
    'RECEIVER',
];

export const ELEMENT_TYPE_LABELS: Record<VoucherTemplateElementType, string> = {
    TEXT: 'Freitext',
    AMOUNT: 'Betrag',
    CODE: 'Code',
    SENDER: 'Von',
    RECEIVER: 'Für',
};

export const ELEMENT_PLACEHOLDER_TEXT: Record<
    VoucherTemplateElementType,
    string
> = {
    TEXT: '',
    AMOUNT: '€100',
    CODE: 'ABCD-1234',
    SENDER: 'Max Mustermann',
    RECEIVER: 'Maria Musterfrau',
};

export const ALIGNMENT_LABELS: Record<VoucherTemplateAlignment, string> = {
    0: 'Links',
    1: 'Mitte',
    2: 'Rechts',
};

export const createDefaultElement = (
    elementType: VoucherTemplateElementType
): VoucherTemplateElement => {
    const defaults: Record<
        VoucherTemplateElementType,
        Pick<VoucherTemplateElement, 'x' | 'y' | 'w' | 'h'>
    > = {
        TEXT: { x: 0.1, y: 0.3, w: 0.8, h: 0.1 },
        AMOUNT: { x: 0.1, y: 0.45, w: 0.3, h: 0.08 },
        CODE: { x: 0.1, y: 0.55, w: 0.3, h: 0.06 },
        SENDER: { x: 0.1, y: 0.65, w: 0.35, h: 0.06 },
        RECEIVER: { x: 0.55, y: 0.65, w: 0.35, h: 0.06 },
    };

    return {
        elementType,
        ...defaults[elementType],
        rotation: 0,
        font: 'Apercu-Light',
        fontSize: 12,
        bold: false,
        italic: false,
        colorR: 0,
        colorG: 0,
        colorB: 0,
        alignment: 0,
    };
};

export const createDefaultElements = (): Record<
    VoucherTemplateElementType,
    VoucherTemplateElement
> => ({
    TEXT: createDefaultElement('TEXT'),
    AMOUNT: createDefaultElement('AMOUNT'),
    CODE: createDefaultElement('CODE'),
    SENDER: createDefaultElement('SENDER'),
    RECEIVER: createDefaultElement('RECEIVER'),
});
